import { ITimeFrame } from 'apis/types';
import { parseBusinessTimeToMinutes } from 'utils/serverTime';

export interface LunchTimeRange {
  startMinute: number;
  endMinute: number;
}

/**
 * 時刻境界を分に変換する。開始の 'Open' は -Infinity、終了の 'Close' は +Infinity として扱う。
 * Convert a time boundary to minutes. 'Open' (start) is -Infinity and 'Close' (end) is +Infinity.
 *
 * @param value - 'HH:MM' / 'Open' / 'Close'
 * @param boundary - 開始境界か終了境界か / Whether the value is a start or end boundary
 * @returns 分。解析できない場合は null / Minutes, or null if the value cannot be parsed
 */
export const parseTimeFrameBoundary = (value: string, boundary: 'start' | 'end'): number | null => {
  if (boundary === 'start' && value === 'Open') return Number.NEGATIVE_INFINITY;
  if (boundary === 'end' && value === 'Close') return Number.POSITIVE_INFINITY;
  return parseBusinessTimeToMinutes(value);
};

export const getLunchTimeRanges = (timeFrames: ITimeFrame[], lunchFrameIds: string[]): LunchTimeRange[] => {
  const lunchFrameIdSet = new Set(lunchFrameIds);

  return timeFrames.reduce<LunchTimeRange[]>((ranges, timeFrame) => {
    if (!lunchFrameIdSet.has(timeFrame.time_frame_id)) return ranges;

    const startMinute = parseTimeFrameBoundary(timeFrame.start, 'start');
    const endMinute = parseTimeFrameBoundary(timeFrame.end, 'end');

    if (startMinute !== null && endMinute !== null && startMinute < endMinute) {
      ranges.push({ startMinute, endMinute });
    }

    return ranges;
  }, []);
};

export const isLunchTime = (businessMinute: number, timeFrames: ITimeFrame[], lunchFrameIds: string[]): boolean => {
  if (!Number.isFinite(businessMinute)) return false;

  return getLunchTimeRanges(timeFrames, lunchFrameIds).some(
    ({ startMinute, endMinute }) => startMinute <= businessMinute && businessMinute <= endMinute
  );
};
